import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Center, Spacer, Spinner } from '@chakra-ui/react';
import { RequestListCard } from '@src/components/sco/Requests/RequestListCard';
import { OnboardingHighlightTarget, ONBOARDING_HIGHLIGHT_IDS } from '@src/components/sco/Requests/RequestOnboarding/OnboardingHighlightTarget';
import { SidebarNestedNavList } from '@src/components/sco/SidebarNestedNavList';
import { GroupRequest } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import React from 'react';
import { LuBadgeDollarSign, LuHand, LuLayoutDashboard, LuLock, LuMessageSquare, LuMessagesSquare, LuMonitor, LuPencilLine, LuSettings, LuShieldQuestion, LuUsers } from 'react-icons/lu';
import { RiTreasureMapLine } from 'react-icons/ri';
import { NavGroup } from '../Advisors/layout/NavGroup';
type SingleRequestSidebarNavProps = {
  groupRequestId: string;
  isManagerView?: boolean;
  /** When false, skip the top RequestListCard (e.g. manager nav uses WorkspaceGoalSwitcher). Default true. */
  showRequestCard?: boolean;
};
export const SingleRequestSidebarNav: React.FC<SingleRequestSidebarNavProps> = props => {
  const $ = _c(22);
  const {
    currentUser
  } = useCurrentUser();
  const showRequestCard = props.showRequestCard !== false;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($groupRequestId: String!) {
        groupRequest(groupRequestId: $groupRequestId) {
          id
          groupRequestId
          canUserEdit
          groupRequestTypeKey
          currentUserGroupCommitment {
            id
            groupCommitmentId
          }
          publishStatusKey
          publishStatus {
            id
            key
            title
          }
          groupCommitments {
            id
            pagination {
              itemCount
            }
          }
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.groupRequestId) {
    t1 = {
      variables: {
        groupRequestId: props.groupRequestId
      }
    };
    $[1] = props.groupRequestId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useQuery(t0, t1);
  if (!query.data?.groupRequest) {
    if (query.loading) {
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <Center padding={4} width="full"><Spinner /></Center>;
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      return t2;
    }
    if (query.error) {
      let t2;
      if ($[4] !== query.error) {
        t2 = query.error.toString();
        $[4] = query.error;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      let t3;
      if ($[6] !== t2) {
        t3 = <Center padding={4}>{t2}</Center>;
        $[6] = t2;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      return t3;
    }
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Center padding={4}><>Not loaded/found.</></Center>;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  const groupRequest = query.data?.groupRequest;
  let t2;
  if ($[9] !== props.groupRequestId || $[10] !== showRequestCard) {
    t2 = showRequestCard && <><OnboardingHighlightTarget borderRadius="md" id={ONBOARDING_HIGHLIGHT_IDS.requestListCard}><RequestListCard groupRequestId={props.groupRequestId} showRequestingUser={false} /></OnboardingHighlightTarget><Spacer height={4} /></>;
    $[9] = props.groupRequestId;
    $[10] = showRequestCard;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== currentUser?.isAdmin || $[13] !== groupRequest.canUserEdit || $[14] !== groupRequest.groupCommitments?.pagination?.itemCount || $[15] !== groupRequest.groupRequestTypeKey || $[16] !== props.groupRequestId || $[17] !== props.isManagerView) {
    t3 = props.isManagerView ? <><SidebarNestedNavList accordionBehavior="expand-only" sections={[{
        title: "Workspace",
        href: `/requests/manage/${props.groupRequestId}`,
        icon: LuLayoutDashboard,
        activeExact: true
      }, {
        title: "Game Plan",
        href: `/requests/manage/${props.groupRequestId}/tasks`,
        icon: RiTreasureMapLine
      }, {
        title: "Edit Goal",
        href: `/requests/manage/${props.groupRequestId}/edit`,
        icon: LuPencilLine
      }].filter(Boolean)} /><Spacer height={4} />{groupRequest.groupRequestTypeKey !== "goal" && <NavGroup label="Apps"><SidebarNestedNavList accordionBehavior="navigate-and-expand" sections={[{
          title: "Advisors",
          href: `/requests/manage/${props.groupRequestId}/advisors`,
          icon: LuMessagesSquare
        }, groupRequest.groupRequestTypeKey !== "funding" && {
          title: "Member Matching",
          href: `/requests/manage/${props.groupRequestId}/member-matching`,
          icon: LuUsers
        }, groupRequest.groupRequestTypeKey == "funding" && {
          title: "Pitch Deck",
          href: `/requests/manage/${props.groupRequestId}/pitch-decks`,
          icon: LuMonitor
        }, groupRequest.groupRequestTypeKey == "funding" && {
          title: "Investors",
          href: `/requests/manage/${props.groupRequestId}/investor-matching`,
          icon: LuBadgeDollarSign,
          items: [{
            title: "Overview",
            href: `/requests/manage/${props.groupRequestId}/investor-matching/overview`
          }, {
            title: "Investor Matches",
            href: `/requests/manage/${props.groupRequestId}/investor-matching/matches`
          }, {
            title: "Search Settings",
            href: `/requests/manage/${props.groupRequestId}/investor-matching/settings/search`
          }, {
            title: "Email Pitch Builder",
            href: `/requests/manage/${props.groupRequestId}/investor-matching/pitch`
          }, {
            title: "Send Messages",
            href: `/requests/manage/${props.groupRequestId}/investor-matching/messages`
          }, {
            title: "Activity & Tracking",
            href: `/requests/manage/${props.groupRequestId}/investor-matching/activity`
          }, {
            title: "Email Settings",
            href: `/requests/manage/${props.groupRequestId}/investor-matching/settings`,
            activeExact: true
          }]
        }].filter(Boolean)} /></NavGroup>}{currentUser?.isAdmin && <><Spacer height={4} /><SidebarNestedNavList accordionBehavior="expand-only" sections={[{
          title: "View in Admin",
          href: `/admin/requests/${props.groupRequestId}`,
          target: "_blank",
          icon: LuLock
        }]} /></>}</> : <SidebarNestedNavList sections={[{
      title: "Request",
      href: `/requests/${props.groupRequestId}`,
      icon: LuShieldQuestion,
      activeExact: true
    }, {
      title: "Offers to Help",
      href: `/requests/${props.groupRequestId}/commitments`,
      icon: LuHand,
      badgeProps: groupRequest.groupCommitments?.pagination?.itemCount > 0 ? {
        children: <span>{groupRequest.groupCommitments?.pagination?.itemCount}</span>
      } : null
    }, {
      title: "Discussion",
      href: `/requests/${props.groupRequestId}/discussion`,
      icon: LuMessageSquare
    }, {
      dividerProps: {}
    }, groupRequest.canUserEdit && {
      title: "Manage this Request",
      href: `/requests/manage/${props.groupRequestId}`,
      icon: LuSettings
    }, currentUser?.isAdmin && {
      title: "View in Admin",
      href: `/admin/requests/${props.groupRequestId}`,
      target: "_blank",
      icon: LuLock
    }].filter(Boolean)} />;
    $[12] = currentUser?.isAdmin;
    $[13] = groupRequest.canUserEdit;
    $[14] = groupRequest.groupCommitments?.pagination?.itemCount;
    $[15] = groupRequest.groupRequestTypeKey;
    $[16] = props.groupRequestId;
    $[17] = props.isManagerView;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== t2 || $[20] !== t3) {
    t4 = <Box flexGrow={1} overflowY="auto">{t2}{t3}</Box>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
};